import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
} from 'mobx';
import * as dateFns from 'date-fns';
import { Categories, NewsResponse } from '@project/API/resources/news';
import { RequestError, Services } from '../../API';
import { MapProperties } from '@datum-map';
import { Query, QueryOptions } from '../Query';
import { INewsCategories, INewsItemProperties } from './types';
import axios from 'axios';
import { SENSITIVE_VALUES } from '../../components/Module/Table/Filters/FilterInput/constants';
import { EntityLoader } from '../EntityLoader';
import { SingleWindow } from '../Window';
import { QueryToNewsListLink } from './QueryToNewsListLink';
import { QueryToVectorSourceLink } from './QueryToVectorSourceLink';
import { Point } from 'ol/geom';
import WKT from 'ol/format/WKT';
import { NewsLayerController } from './LayerController';
import { LoadFeatureCollectionCallback } from '../SourceLoaderDecorator';
import { FilterSearchGroups } from '../Query/Filter';
import { UploadFile } from 'antd/lib/upload/interface';

export type NewsFilteredFields =
  | 'title'
  | 'is_checked'
  | 'is_archived'
  | 'date_start'
  | 'date_end'
  | 'category'
  | 'date_planning';

export type FileError = { file: UploadFile; message: string };

const DEFAULT_QUERY: QueryOptions<NewsFilteredFields> = {
  page: 1,
  pageSize: 10,
  search: undefined,
  order: [{ field: 'date_start', direction: '-' }],
  filter: {
    orParams: [
      [
        {
          field: 'date_end',
          operator: SENSITIVE_VALUES.gte,
          value: dateFns.format(new Date(), 'yyyy-MM-dd'),
        },

        { field: 'is_checked', value: 'true' },
        { field: 'is_archived', value: 'false' },
      ],
    ],
  },
  isLimitOffsetPagination: false,
};

export type FileErrors = {
  uploadErrors: FileError[];
  deleteErrors: FileError[];
};

/* TODO: Отображать статус для каждой отдельной новости */
export class NewsStore {
  readonly newsCard = new SingleWindow<INewsItemProperties>();
  readonly newsAdd = new SingleWindow<GeoJSON.Position>();
  readonly newsEdit = new SingleWindow<NewsResponse>();
  readonly layerController: NewsLayerController;
  readonly queryListLink: QueryToNewsListLink;
  readonly queryLayerLink: QueryToVectorSourceLink<NewsFilteredFields>;
  readonly newsCategories: INewsCategories;
  readonly query: Query<NewsFilteredFields>;

  @observable private _isLoading = false;
  @observable private _news: NewsResponse[] = [];
  @observable private _error?: string = undefined;
  @observable private _totalNewsCount = 0;
  @observable private _isBulkPublishEnabled = false;
  @observable bulkPublishSelectedNews: Set<number> = new Set<number>();

  private _services: Services;
  private _newsLoaderSignals = new Set<AbortController>();

  constructor(services: Services, private _mapProperties: MapProperties) {
    this._services = services;
    this.layerController = new NewsLayerController(
      _mapProperties.map,
      this._loadFeatures,
      { mode: 'viewing' },
    );
    this.query = new Query<NewsFilteredFields>(DEFAULT_QUERY);

    this.newsCategories = new EntityLoader<Categories[]>({
      load: this._services.newsCategories.get,
    });

    this.newsCategories.load();

    this.queryListLink = new QueryToNewsListLink(this.query, this.loadNews);
    this.queryLayerLink = new QueryToVectorSourceLink(
      this.query,
      this.layerController.source,
    );

    this.queryLayerLink.bind();

    makeObservable(this);
  }

  @computed get news() {
    return this._news;
  }

  @computed get isBulkPublishEnabled(): boolean {
    return this._isBulkPublishEnabled;
  }

  @computed get totalNewsCount() {
    return this._totalNewsCount;
  }

  @computed get error() {
    return this._error;
  }

  @computed get isLoading() {
    return this._isLoading;
  }

  @action enabled = () => {
    this._isBulkPublishEnabled = true;
  };

  @action hide = () => {
    this._isBulkPublishEnabled = false;
  };

  @action setSelectedNews = (newsIds: number[] | undefined) => {
    if (newsIds) {
      this.bulkPublishSelectedNews = new Set(newsIds);
    } else {
      this.bulkPublishSelectedNews = new Set();
    }
  };

  @action addBulkSelectedNews = (newsId: number) => {
    this.bulkPublishSelectedNews.add(newsId);
  };

  @action removeBulkSelectedNews = (newsId: number) => {
    this.bulkPublishSelectedNews.delete(newsId);
  };

  @action loadNews = async (params?: URLSearchParams) => {
    if (this._isLoading) {
      this._newsLoaderSignals.forEach((s) => s.abort());
      this._newsLoaderSignals = new Set();
    }

    const currentSignal = new AbortController();
    this._newsLoaderSignals.add(currentSignal);

    runInAction(() => {
      this._isLoading = true;
      this._error = undefined;
    });

    try {
      const result = await this._services.news.get(
        params,
        currentSignal.signal,
      );

      runInAction(() => {
        this._news = result.results;
        this._totalNewsCount = result.count;
      });
    } catch (e) {
      if (!axios.isCancel(e)) {
        runInAction(() => {
          this._news = [];
          this._error = 'Произошла ошибка при загрузке новостей';
        });
      }
    } finally {
      this._newsLoaderSignals.delete(currentSignal);

      runInAction(() => {
        this._isLoading = this._newsLoaderSignals.size > 0;
      });
    }
  };

  @action deleteNews = async (id: number) => {
    await this._services.news.delete(id);

    const source = this.layerController.source;
    const feature = source.getFeatureById(id);

    if (feature) {
      source.removeFeature(feature);
    }

    const item = this._news.findIndex((v) => v.id === id);

    if (item === -1) return;

    this._news.splice(item, 1);

    runInAction(() => {
      this._totalNewsCount = this._totalNewsCount - 1;
    });

    await this._updatePage();
  };

  @action addNews = async (
    news: NewsResponse,
    filesToUpload?: UploadFile[],
  ) => {
    const response = await this._services.news.create({
      ...news,
      photos: undefined,
    });

    let uploadErrors: FileError[] = [];
    if (response.id) {
      if (filesToUpload?.length) {
        uploadErrors = await this.uploadFiles(response.id, filesToUpload);
      }
    }

    await this.loadNews(this.query.searchParams);

    this.layerController.source.refresh();

    return { id: response.id, uploadErrors };
  };

  private _updatePage = async () => {
    const isEmptyPage = this._news.length === 0;
    const hasNewsItems = this._totalNewsCount > 0;

    if (!isEmptyPage || (isEmptyPage && this.query.page === 1)) {
      const result = await this._services.news.get(this.query.searchParams);

      runInAction(() => {
        this._news = result.results;
        this._totalNewsCount = result.count;
      });

      return;
    }

    // Запрос будет отправлен в useEffect
    if (hasNewsItems && this.query.page > 1) {
      this.query.setPage(this.query.page - 1);
    }
  };

  @action updateNews = async (
    news: Partial<Omit<NewsResponse, 'geom'>> & {
      id: number;
      geom?: GeoJSON.Point;
    },
    filesToUpload?: UploadFile[],
    filesToRemove?: UploadFile[],
  ): Promise<FileErrors> => {
    const view = this._mapProperties.map.getView();
    const projection = view.getProjection().getCode();
    const wkt = news.geom ? this._geomToWkt(news.geom, projection) : undefined;

    const response = await this._services.news.patch({
      ...news,
      geom: wkt,
    });

    let uploadErrors: FileError[] = [];
    let deleteErrors: FileError[] = [];
    if (response.id) {
      if (filesToUpload?.length) {
        uploadErrors = await this.uploadFiles(response.id, filesToUpload);
      }

      if (filesToRemove?.length) {
        deleteErrors = await this.deleteFiles(filesToRemove);
      }
    }

    this.loadNews(this.query.searchParams);
    this.layerController.source.refresh();

    return { uploadErrors, deleteErrors };
  };

  uploadFiles = async (newsId: number, fileList: UploadFile[]) => {
    const errors: FileError[] = [];

    const uploads = fileList.map((file) => {
      return async () => {
        try {
          await this._services.newsPhotos.post(file, newsId);
        } catch (e) {
          let message = `Ошибка загрузки файла${': ' + (file.name ?? '')}.`;

          if (e instanceof RequestError) {
            message = `Ошибка загрузки файла${': ' + (file.name ?? '')}. ${
              Array.isArray(e.fields?.['photo'])
                ? e.fields?.['photo'].join(' ')
                : e.fields?.['photo']
            }`;
          }

          errors.push({
            file,
            message,
          });
        }
      };
    });

    await Promise.allSettled(uploads.map((p) => p()));

    return errors;
  };

  deleteFiles = async (fileList: UploadFile[]) => {
    const errors: FileError[] = [];

    const deletes = fileList.map((file) => {
      return async () => {
        try {
          await this._services.newsPhotos.delete(Number(file.uid));
        } catch (e) {
          let message = `Ошибка удаления файла${': ' + (file.name ?? '')}.`;

          if (e instanceof RequestError) {
            message = `Ошибка удаления файла${': ' + (file.name ?? '')}. ${
              Array.isArray(e.fields?.['photo'])
                ? e.fields?.['photo'].join(' ')
                : e.fields?.['photo']
            }`;
          }

          errors.push({
            file,
            message,
          });
        }
      };
    });

    await Promise.allSettled(deletes.map((p) => p()));

    return errors;
  };

  @action bulkPublicateNews = async () => {
    const selectedNewsIds = [...this.bulkPublishSelectedNews];

    await this._services.newsBulkPublication.put(selectedNewsIds);

    this.layerController.source.refresh();

    selectedNewsIds.forEach((selectedNewsId) => {
      this.bulkPublishSelectedNews.delete(selectedNewsId);

      const item = this._news.findIndex((v) => v.id === selectedNewsId);

      if (item === -1) return;

      this._news.splice(item, 1);

      runInAction(() => {
        this._totalNewsCount = this._totalNewsCount - 1;
      });
    });

    await this._updatePage();
  };

  // ToDo: Использовать enum?
  static isFilteredFields = (value: string): value is NewsFilteredFields => {
    return [
      'title',
      'is_checked',
      'is_archived',
      'date_start',
      'date_end',
      'category',
      'date_planning',
    ].some((v) => value === v);
  };

  getNewsById = async (id: number) => {
    const params = new URLSearchParams({ id: String(id) });

    const result = await this._services.news.get(params);

    return result.results[0];
  };

  private _geomToWkt = (
    geom: Point | GeoJSON.Point | string,
    fromProj: string,
  ): string => {
    if (typeof geom === 'string') {
      return geom;
    }

    const toProj = 'EPSG:4326';

    if (geom instanceof Point) {
      const geomIn4326 = geom.clone().transform(fromProj, toProj);
      const wktGeometry = new WKT().writeGeometry(geomIn4326);

      return 'SRID=4326;' + wktGeometry;
    }

    const olGeom = new Point(geom.coordinates);
    const geomIn4326 = olGeom.clone().transform(fromProj, toProj);
    const wktGeometry = new WKT().writeGeometry(geomIn4326);

    return 'SRID=4326;' + wktGeometry;
  };

  private _loadFeatures: LoadFeatureCollectionCallback = async (config) => {
    const params = new URLSearchParams(config.params.toString());
    const extraParams = this._buildExtraParams();

    if (extraParams) {
      extraParams.forEach((value, key) => {
        params.set(key, value);
      });
    }

    const response = await this._services.newsGeoJSONLayer.get({
      ...config,
      params,
    });

    return response;
  };

  private _buildExtraParams = () => {
    if (!this.query) return undefined;

    const { filter, search } = this.query;

    const filterSearchParams = new FilterSearchGroups(filter.value).toString();
    const params = new URLSearchParams(filterSearchParams);

    if (search) params.set('_search', search);

    return params;
  };
}
